/* global unescape */

var base = require('./base'),
    baseURL = base.getBaseURL(),
    marshallTypedArray = base.marshallTypedArray,
    createImageFromData = base.createImageFromData,
    revokeDataURL = base.revokeDataURL,
    createURLFromDataAndReleaseChunk = base.createURLFromDataAndReleaseChunk;    

var appExports = {
    /**
     * The namespace represents an auxiliary interface.
     * 
     * @namespace
     * @memberof module:solo.app
     */
    util: {
        /**
         * Fetches resource from the *.cortona3d* bundle using {@link https://developer.mozilla.org/en-US/docs/Web/API/URL URL API} and returns a string containing an object URL representing given resource.
         * You must explicitly release the object URL after its use by calling {@link module:solo.app.util.revokeResourceURL app.util.revokeResourceURL()}.
         * 
         * @param {string} name - Name of resource in the *.cortona3d* bundle
         * @returns {string} An object URL
         * @see external:URL
         * @see {@link module:solo.app.util.revokeResourceURL app.util.revokeResourceURL()}
         */
        createResourceURL: function (name) {
            if (!Module.ccall) return;
            if (name.indexOf(':') >= 0) return;

            var src = decodeURI(name),
                chunk = Module.ccall('tiramisu_copyResourceFromBundle', 'number', ['string'], [src]),
                options = {};

            if (/\.svg$/i.test(src)) {
                options.type = 'image/svg+xml';
            }

            return createURLFromDataAndReleaseChunk(chunk, options);
        },

        /**
         * Releases the existing object URL, which was previously created by calling {@link module:solo.app.util.createResourceURL}.
         * 
         * @param {URL} url - The object URL
         * @see external:URL
         * @see {@link module:solo.app.util.createResourceURL app.util.createResourceURL()}
         */
        revokeResourceURL: function (url) {
            if (!url || !/^blob:/i.test(url)) return;
            revokeDataURL(url);
        },

        /**
         * Fetches an image from the *.cortona3d* bundle and returns an Image object.
         * 
         * @param {HTMLImageElement|string} image - The HTML image element with 'src' attribute equals to the name of the image in the bundle or the name of the image itself
         * @param {number} [autoRevokeTimeout=0] - The lifetime of an object URL in milliseconds. A value less than zero disables auto revoke
         * @returns {HTMLImageElement} Image object
         */
        fetchImageFromDocumentBundle: function (image, autoRevokeTimeout) {
            var img,
                name,
                options;

            if (typeof image === 'string') {
                img = new Image();
                name = image;
            } else {
                img = image;
                name = img.getAttribute('src');
                if (name.lastIndexOf(baseURL, 0) === 0) {
                    name = name.slice(baseURL.length);
                }
            }

            name = decodeURI(name).replace(/\.cgm$/i, '.svg');
            options = /\.svg$/i.test(name) ? {
                type: 'image/svg+xml'
            } : {};

            return createImageFromData(Module.ccall('tiramisu_copyResourceFromBundle', 'number', ['string'], [name]), img, options, autoRevokeTimeout);
        },

        /**
         * Fetch banner image from the VRML file.
         *
         * @returns {BannerImageInfo} An object that represents the information for the banner image
         */
        fetchBannerImage: function () {
            var sp = stackSave(),
                params = stackAlloc(8),
                image = createImageFromData(Module._tiramisu_copyBannerImage(params)),
                p = params >>> 2,
                result;
            if (image) {
                result = {
                    image: image,
                    frameCount: HEAP32[p],
                    frameDuration: HEAP32[p + 1] / 1000
                };
            }
            stackRestore(sp);
            return result;
        },
        /**
         * @class The constructor creates an instance of {@link Vec3f} object corresponds to a VRML SFVec3f field.
         * @see Vec3f
         * @since 1.4.0
         */
        Vec3f: require('lib/vec3f'),
        /**
         * @class The constructor creates an instance of {@link Rotation} object corresponds to a VRML SFRotation field.
         * @see Rotation
         * @since 1.4.0
         */
        Rotation: require('lib/rotation'),
        /**
         * Decompress typedArray with inflate/ungzip.
         * 
         * @param {Uint8Array} typedArray
         * @returns {Uint8Array|null} Decompressed data or null
         * @see {@link module:solo.app.util.freeTypedArray app.util.freeTypedArray()}
         * @since 1.6.6
         */
        inflate: function (typedArray) {
            var data = marshallTypedArray(typedArray),
                chunk = Module._tiramisu_utility_inflate(data.byteOffset, data.length);
            Module._free(data.byteOffset);
            return chunk ? HEAPU8.subarray(chunk + 4, chunk + 4 + HEAPU32[chunk >>> 2]) : null;
        },
        /**
         * Free the existing TypedArray data, which was previously created by calling {@link module:solo.app.util.inflate app.util.inflate()}.
         * 
         * @param {Uint8Array} typedArray
         * @see {@link module:solo.app.util.inflate app.util.inflate()}
         * @since 1.6.6
         */
        freeTypedArray: function (typedArray) {
            if (typedArray) {
                Module._free(typedArray.byteOffset - 4);
            }
        }
    },
};

module.exports = {
    app: appExports
};

/**
 * The object represents a banner image from a VRML file, for example:
 * ```
 * {
 *      image: <Image object>,
 *      frameCount: 0,
 *      frameDuration: 0
 * }
 * ```
 * @prop {HTMLImageElement} image - An image object. 
 * @prop {number} frameCount - Number of frames for animation. 
 * @prop {number} frameDuration - Duration of animation of one frame.
 * @typedef {object} BannerImageInfo
 * @see {@link module:solo.app.util.fetchBannerImage app.util.fetchBannerImage()}
 */